/**
 * Global patch for Supabase Edge Function invocations.
 *
 * Problem: `supabase.functions.invoke()` wraps any non-2xx response in a
 * `FunctionsHttpError` whose `.message` is the unhelpful constant
 * "Edge Function returned a non-2xx status code". This leaks to UI toasts
 * across the entire app whenever an edge function returns a structured 4xx
 * (e.g. "Invalid OTP", "Insufficient funds", "Profile not found").
 *
 * Fix: monkey-patch `functions.invoke` on every Supabase client so that, on
 * error, we read the response body via `error.context` (a Response object)
 * and replace `error.message` with the real `body.error` / `body.message`
 * field returned by the edge function. The original Response is preserved.
 *
 * This is idempotent — calling it multiple times on the same client is a no-op.
 */

const PATCH_FLAG = Symbol.for('paynovus.invoke.patched');

type AnyClient = {
  functions: {
    invoke: (...args: any[]) => Promise<{ data: any; error: any }>;
  };
  [k: string]: any;
};

export function patchSupabaseInvoke(client: AnyClient): void {
  if (!client?.functions?.invoke) return;
  if ((client.functions as any)[PATCH_FLAG]) return;

  const original = client.functions.invoke.bind(client.functions);

  client.functions.invoke = async (...args: any[]) => {
    const result = await original(...args);
    const err = result?.error;
    if (!err) return result;

    // FunctionsHttpError exposes the raw Response on `.context`
    const ctx: any = (err as any).context;
    if (!ctx || typeof ctx !== 'object') return result;

    try {
      // Response can only be read once; clone to be safe for downstream code
      const cloned = typeof ctx.clone === 'function' ? ctx.clone() : ctx;
      let parsed: any = null;
      const ct = cloned.headers?.get?.('content-type') || '';

      if (ct.includes('application/json') && typeof cloned.json === 'function') {
        try { parsed = await cloned.json(); } catch { /* fall through */ }
      }
      if (parsed == null && typeof cloned.text === 'function') {
        try {
          const txt = await cloned.text();
          if (txt) {
            try { parsed = JSON.parse(txt); }
            catch { if (txt.length < 500) parsed = { error: txt }; }
          }
        } catch { /* ignore */ }
      }

      const realMsg =
        (typeof parsed === 'string' && parsed) ||
        parsed?.error ||
        parsed?.message ||
        parsed?.error_description ||
        (Array.isArray(parsed?.errors) && parsed.errors[0]?.message);

      if (realMsg && typeof realMsg === 'string') {
        try { (err as any).message = realMsg; } catch { /* read-only — skip */ }
        try { (err as any).serverMessage = realMsg; } catch { /* ignore */ }
        try { (err as any).body = parsed; } catch { /* ignore */ }
      }
    } catch {
      // Never let the patch itself throw
    }

    return result;
  };

  (client.functions as any)[PATCH_FLAG] = true;
}
